import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StudyEntryModel } from '../model/study-entry.model';
import { SERVER_API_URL } from '../../../app.constants';
import { ParamContext } from '../../service/param.context';
import { ObservationVariable } from '../../model/observation-variable.model';
import { StudyEntryRequestModel } from '../model/study-entry-request.model';
import { map } from 'rxjs/operators';
import { getAllRecords } from '../../util/get-all-records';
import { createRequestOption } from '../../model/request-util';
import { StudyEntryColumn } from '../model/study-entry-column.model';
import { EnableLoadingIndicator, EnableSavingIndicator } from '../../loading-indicator';

// Interfaces
export class StudyEntrySearchParams {
    page?: number;
    size?: number;
    sort?: string;
    searchDTO?: StudyEntryRequestModel;
    germplasmDescriptorIds?: number[];
}

export interface PaginatedResponse<T> {
    data: T[];
    filteredCount: number;
    totalCount: number;
    page: number;
    size: number;
}

@Injectable({
    providedIn: 'root'
})
export class StudyEntryService {

    constructor(
        private http: HttpClient,
        private paramContext: ParamContext // You'll need to create/inject this
    ) {
    }

    getStudyEntries(
        studyId: number,
        searchParams: StudyEntrySearchParams
    ): Observable<PaginatedResponse<StudyEntryModel>> {
        let url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries`;

        let params = new HttpParams();
        if (searchParams.page !== undefined && searchParams.size !== undefined) {
            params = params
                .set('page', searchParams.page.toString())
                .set('size', searchParams.size.toString());
        }

        if (searchParams.sort) {
            params = params.set('sort', searchParams.sort);
        }

        if (searchParams.germplasmDescriptorIds?.length) {
            searchParams.germplasmDescriptorIds.forEach(id => {
                params = params.append('germplasmDescriptorIds', id.toString());
            });
        }

        return this.http.post<StudyEntryModel[]>(
            url,
            searchParams.searchDTO || {},
            {
                params,
                observe: 'response',
                context: new HttpContext().set(EnableLoadingIndicator, true)
            }
        ).pipe(
            map(response => {
                return {
                    data: response.body || [],
                    filteredCount: Number(response.headers.get('X-Filtered-Count')),
                    totalCount: Number(response.headers.get('X-Total-Count')),
                    page: searchParams.page,
                    size: searchParams.size
                } as PaginatedResponse<StudyEntryModel>;
            })
        );
    }

    getAllStudyEntries(studyId: number, sort: any, germplasmDescriptorIds?: number[]): Observable<StudyEntryModel[]> {
        let url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries`;
        if (germplasmDescriptorIds && germplasmDescriptorIds.length) {
            url += '?germplasmDescriptorIds=' + germplasmDescriptorIds;
        }
        return getAllRecords<StudyEntryModel>((page: number, pageSize: number) => {
            const params = createRequestOption(Object.assign({ page, size: pageSize, sort }));
            return this.http.post<StudyEntryModel[]>(url, null, { params, observe: 'response' });
        });
    }

    getStudyGermplasmListDetails(studyId: number): Observable<any> {
        return this.http.get<StudyEntryModel>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/list/details`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    replaceStudyGermplasm(studyId: number, entryId: number, newGid: number): Observable<any> {
        return this.http.put(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/${entryId}`, {
            gid: newGid
        }, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    getEntryTableColumns(studyId: number): Observable<ObservationVariable[]> {
        return this.http.get<ObservationVariable[]>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/table/columns`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getEntryTypes(): Observable<any> {
        return this.http.get(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/entry-types`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    saveStudyEntries(studyId: number, entryTypeId: number, itemIds: number[]): Observable<any> {
        return this.http.put(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/`, {
            entryTypeId,
            searchComposite: {
                itemIds,
                searchRequest: null
            }
        }, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    saveStudyEntriesList(studyId: number, listId: number): Observable<any> {
        return this.http.post(
            `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/generation?listId=${listId}`,
            null, { context: new HttpContext().set(EnableSavingIndicator, true) }
        );
    }

    deleteEntries(studyId: number): Observable<any> {
        return this.http.delete(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries`, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    updateStudyEntriesProperty(
        studyId: number,
        entryIds: number[],
        variableId: number,
        newValue: any
    ): Observable<any> {
        return this.http.put(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/properties`, {
            searchComposite: {
                itemIds: entryIds,
                searchRequest: null
            },
            variableId,
            value: newValue
        }, { context: new HttpContext().set(EnableSavingIndicator, true) });
    }

    getStudyEntriesMetadata(studyId: number): Observable<StudyEntryMetadata> {
        return this.http.get<StudyEntryMetadata>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/metadata`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    fillWithCrossExpansion(studyId: number, generationLevel: string): Observable<any> {
        return this.http.put(
            `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/pedigree-generation-level`,
            generationLevel
            , { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getCrossLevelGeneration(studyId: number): Observable<any> {
        return this.http.get(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/pedigree-generation-level`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    getStudyEntriesColumns(studyId: number): Observable<StudyEntryColumn[]> {
        return this.http.get<StudyEntryColumn[]>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries/columns`, { context: new HttpContext().set(EnableLoadingIndicator, true) });
    }

    async getStudyEntriesCount(studyId: number): Promise<number> {
        let url = SERVER_API_URL + `crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/entries`;
        const response = await this.http.head(url, { observe: 'response' }).toPromise();
        const totalCount = response?.headers?.get('X-Total-Count') || 0;
        return Number(totalCount);
    }

}

export class StudyEntryMetadata {
    testEntriesCount: number;
    checkEntriesCount: number;
    nonTestEntriesCount: number;
    nonReplicatedEntriesCount: number;
    sisterLinesEntriesCount: number;
    hasUnassignedEntries: boolean;
}
